import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { useNavigate } from 'react-router-dom';

// COMPONENTS
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { AreaButton } from '@/main/components/shared/styles';

// SERVICES / REDUX / UTILS
import { IntegrationInterfaceCompanyRequest } from '@/main/models/request';
import { getOptionsStatusIntegrationCompany } from '@/main/services/data/data';
import { KeyValue } from '@/main/models/common';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isValueQueryParams,
  mountReducerWithData,
  removeEmptyValues,
  getDefaultOptionDrop
} from '@/main/services/utils';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getCompany,
  resetReducerCompany,
  useCompanyReducer
} from '@/main/store/company';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import {
  getPayloadFormat,
  useRequestFormatReducer
} from '@/main/store/RequestFormat';
import { StatusIntegrationCompanyEnum } from '@/main/services/enum/StatusIntegrationCompanyEnum';
import {
  getIntegrationInterface,
  getService,
  resetReducerIntegrationInterface,
  resetReducerService,
  useIntegrationTypeReducer
} from '@/main/store/integrationType';
import { userCompanyType, userIsAdmin } from '@/main/services/utilsUser';
import useFormQueryParams from '@/main/hooks/useformQueryParams';

// SCREENS/COMPONENTS/CONTEXT LOCAL
import * as S from './styles';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { useUserReducer } from '@/main/store/user';
interface IFiltersSearchIntegrationProps {
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  onResetSearch: () => void;
  getDataIntegrationInterfaceCompany: (
    filters: IntegrationInterfaceCompanyRequest
  ) => void;
}
// #region Constants
const INITIAL_FORM_STATE = {
  country: null as KeyValue | null,
  companyType: null as KeyValue | null,
  company: null as KeyValue | null,
  service: null as KeyValue | null,
  interface: null as KeyValue | null,
  status: null as KeyValue | null,
};

export type IFormFiltersSearchIntegration = typeof INITIAL_FORM_STATE;
// #endregion

function FiltersSearchIntegration({
  showFilters,
  setShowFilters,
  getDataIntegrationInterfaceCompany,
  onResetSearch
}: IFiltersSearchIntegrationProps) {
  // #region Reducers/Options
  const {
    service: serviceReducer,
    integrationInterface: intergrationInterfaceReducer
  } = useIntegrationTypeReducer();
  const { countriesUser: countryReducer } = useCountryReducer();
  const { company: companyReducer } = useCompanyReducer();
  const { companyType: companyTypeReducer } = useCompanyTypeReducer();
  const [payloadFormats, setPayloadFormats] = useState(true);
  const [isLoadingPayloadFormats, setIsLoadingPayloadFormats] = useState(false);
  const optionsStatus = getOptionsStatusIntegrationCompany();
  const requestFormatReducer = useRequestFormatReducer();
  const userReducer = useUserReducer();

  // #endregion

  // #region Hooks
  const {
    control,
    queryParamsValues,
    reset,
    setValue,
    customHandleSubmit,
    watch
  } = useFormQueryParams<IFormFiltersSearchIntegration>({
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmitFilter,
    mapDefaultValues: {
      company: companyReducer,
      companyType: companyTypeReducer,
      country: countryReducer,
      interface: intergrationInterfaceReducer,
      service: serviceReducer,
      status: mountReducerWithData(optionsStatus),
    }
  });
  const dispatch = useDispatch();
  // #endregion

  // #region States/Watch
  const watcherCompanyType = watch('companyType');
  const watcherCountry = watch('country');
  const watcherIntegrationService = watch('service');
  const typeCompanyUser = userIsAdmin() ? null : userCompanyType();

  const optionDefaultCountry = getDefaultCountryOptionDrop(countryReducer.data);
  const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(
    companyTypeReducer.data,
    typeCompanyUser
  );
  const optionDefaultStatus = optionsStatus.find(
    item => item.key === StatusIntegrationCompanyEnum.Active
  )!;

  // #endregion

  // #region Async Functions
  async function onSubmitFilter(data: IFormFiltersSearchIntegration) {
    const isStatusActive =
      Number(data.status?.key) === StatusIntegrationCompanyEnum.Active;

    const mountRequest: Omit<
      IntegrationInterfaceCompanyRequest,
      'pageIndex' | 'pageSize'
    > = removeEmptyValues({
      guidCompany: String(data.company?.key),
      guidCountry: String(data.country?.key),
      idCompanyType: data.companyType?.key,
      guidService: String(data.service?.key),
      guidInterface: String(data.interface?.key),

      ...(!isNullUndefined(data.status) && { idStatus: isStatusActive })
    });

    getDataIntegrationInterfaceCompany(mountRequest);

    setShowFilters(false);
  }

  async function callIntegrationInterface(service: KeyValue | null) {
    if (!isValueQueryParams(service)) {
      setValue('interface', null);
      dispatch(resetReducerIntegrationInterface());
    }
    if (service === null) return;

    dispatch(
      getIntegrationInterface({
        idIntegrationType: 0,
        guidIntegration: String(service.key)
      })
    );
  }

  async function callCompanyReducer(
    valueSelected: KeyValue | null,
    countrySelected: KeyValue | null = null
  ) {
    if (
      !isValueQueryParams(valueSelected) ||
      !isValueQueryParams(countrySelected)
    ) {
      setValue('company', null);
    }
    const countryKey = isNullUndefined(countrySelected)
      ? String(watcherCountry?.key)
      : String(countrySelected?.key);
    if (valueSelected === null) return;

    dispatch(
      getCompany({
        guidCountry: countryKey,
        idCompanyType: String(valueSelected?.key),
        idStatusRegister: StatusRegisterEnum.Active,
        guidCompany: !userIsAdmin() ? userReducer.currentUser?.guidCompany : null
      })
    );
  }
  // #endregion

  // #region Handle Functions
  function handleClear() {
    reset(INITIAL_FORM_STATE);
    onResetSearch();
    setValue('country', optionDefaultCountry);
    setValue('status', optionDefaultStatus);
    dispatch(resetReducerService());
    // Get services withot filter integration type
    dispatch(getService(0));
  }
  // #endregion

  // #region UseEffects
  useEffect(() => {
    dispatch(getCountriesUser());
    // Get services withot filter integration type
    dispatch(getService(0));
    dispatch(getCompanyType());

    return () => {
      dispatch(resetReducerCompany());
      dispatch(resetReducerService());
      dispatch(resetReducerIntegrationInterface());
    };
  }, []);

  useEffect(() => {
    if (isNullUndefined(watcherCountry)) return;
    callCompanyReducer(optionDefaultCompanyTypeDrop);
  }, [watcherCountry?.key, optionDefaultCompanyTypeDrop?.key]);


  // useEffect to load queryParams
  useEffect(() => {
    const mountCountryQueryParams = isNullUndefined(queryParamsValues.country)
      ? null
      : queryParamsValues.country;
    const mountCompanyTypeQueryParams = isNullUndefined(
      queryParamsValues.companyType
    )
      ? null
      : queryParamsValues.companyType;
    const mountServiceQueryParams = isNullUndefined(queryParamsValues.service)
      ? null
      : queryParamsValues.service;

    if (!isNullUndefined(mountCompanyTypeQueryParams))
      callCompanyReducer(mountCompanyTypeQueryParams, mountCountryQueryParams);
    if (!isNullUndefined(mountServiceQueryParams))
      callIntegrationInterface(mountServiceQueryParams);
  }, [JSON.stringify(queryParamsValues)]);
  // #endregion

  const disabledCountry = countryReducer?.data?.length === 1;
  const disabledIntegrationInterface = isNullUndefined(
    watcherIntegrationService
  );
  const disabledCompany =
    isNullUndefined(watcherCompanyType) ||
    isNullUndefined(watcherCountry) ||
    companyReducer.data?.length === 1;
  const disabledCompanyType =
    isNullUndefined(companyTypeReducer.data) ||
    !isNullUndefined(optionDefaultCompanyTypeDrop);

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <form onSubmit={customHandleSubmit} noValidate>
        <S.Container>
          <KogAutoCompleteControlled
            id="id-searchIntegration-select-country"
            nameCustom="country"
            label={getTranslateByKey('information.country')}
            isDefaultValueUnique
            control={control}
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            options={countryReducer.data}
            isLoading={countryReducer.isLoading}
            disabled={disabledCountry}
            onChangeCustom={() => { }}
          />
          <KogAutoCompleteControlled
            id="id-searchIntegration-select-companyType"
            nameCustom="companyType"
            label={getTranslateByKey('information.companyType')}
            isDefaultValueUnique
            control={control}
            rulesCustom={{ required: false }}
            setValueCustom={setValue}
            options={companyTypeReducer.data}
            isLoading={companyTypeReducer.isLoading}
            disabled={disabledCompanyType}
            valueDefaultKey={optionDefaultCompanyTypeDrop?.key}
            onChangeCustom={callCompanyReducer}
          />
          <KogAutoCompleteControlled
            id="id-searchIntegration-select-company"
            nameCustom="company"
            label={getTranslateByKey('information.company')}
            isDefaultValueUnique
            control={control}
            setValueCustom={setValue}
            options={companyReducer.data}
            isLoading={companyReducer.isLoading}
            disabled={disabledCompany}
            onChangeCustom={() => { }}
          />
          <KogAutoCompleteControlled
            id="id-searchIntegration-select-service"
            nameCustom="service"
            label={getTranslateByKey('information.service')}
            isDefaultValueUnique
            control={control}
            setValueCustom={setValue}
            options={serviceReducer.data}
            isLoading={serviceReducer.isLoading}
            onChangeCustom={callIntegrationInterface}
          />

          <KogAutoCompleteControlled
            id="id-searchIntegration-select-interface"
            nameCustom="interface"
            label={getTranslateByKey('information.interface')}
            isDefaultValueUnique
            control={control}
            setValueCustom={setValue}
            options={intergrationInterfaceReducer.data}
            isLoading={intergrationInterfaceReducer.isLoading}
            disabled={disabledIntegrationInterface}
            onChangeCustom={() => { }}
          />
          <KogAutoCompleteControlled
            id="id-searchIntegration-select-status"
            nameCustom="status"
            label={getTranslateByKey('information.status')}
            valueDefaultKey={optionDefaultStatus?.key}
            control={control}
            rulesCustom={{ required: false }}
            setValueCustom={setValue}
            options={optionsStatus}
            disabled={false}
            onChangeCustom={() => { }}
          />
          <S.AreaButtons>
            <SecondaryButton id="id-searchIntegration-btn-submit" type="submit">
              {getTranslateByKey('information.search')}
            </SecondaryButton>
            <SecondaryButton
              id="id-searchIntegration-btn-clear"
              type="button"
              onClick={handleClear}>
              {getTranslateByKey('information.clear')}
            </SecondaryButton>
          </S.AreaButtons>
        </S.Container>
      </form>
    </Box>
  );
}

function FiltersIntegrationWrapper(
  props: Omit<IFiltersSearchIntegrationProps, 'showFilters' | 'setShowFilters'>
) {
  const navigate = useNavigate();

  //#region States
  const [showFilters, setShowFilters] = useState<boolean>(true);
  //#endregion

  //#region HandleFunctions
  function handleFilters() {
    setShowFilters(prev => !prev);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function handleNewIntegration(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    e.preventDefault();
    e.stopPropagation();
    const redirectRoute = RoutesConstants.integrationRegister;
    navigate(redirectRoute);
  }
  //#endregion

  const mergedProps: IFiltersSearchIntegrationProps = {
    ...props,
    showFilters,
    setShowFilters
  };
  return (
    <>
      <AreaButton>
        <Button onClick={handleFilters} id="id-integrationQueue-btn-filters">
          {getTranslateByKey('information.filters')}
        </Button>
        <Button
          onClick={handleNewIntegration}
          id="id-integrationQueue-btn-filters">
          {getTranslateByKey('information.newIntegration')}
        </Button>
      </AreaButton>
      <FiltersSearchIntegration {...mergedProps} />
    </>
  );
}
export default FiltersIntegrationWrapper;
